import { useState } from 'react';
import { Project } from '@bulthaup/api/db';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { trpc } from '../../../../../../services/trpc';

type UpdateFriezeFieldInput = {
  id: string;
  field: string;
  value: boolean;
};

type BlockingCondition = {
  title: string;
  requirements: string[];
};

type FieldConfig = {
  successMessage: string;
  getBlockingCondition?: (project: Project) => BlockingCondition | null;
};

type UseBooleanButtonStateParams<Field extends string> = {
  project: Project;
  fieldConfigs: Record<Field, FieldConfig>;
};

export function useBooleanButtonState<Field extends string>({
  project,
  fieldConfigs,
}: UseBooleanButtonStateParams<Field>) {
  const utils = trpc.useUtils();
  const [pendingField, setPendingField] = useState<Field | null>(null);

  const updateFriezeField = trpc.projects.updateFriezeField.useMutation({
    onSuccess: (_data, variables) => {
      const config = fieldConfigs[variables.field as Field];
      notifications.show({
        title: 'Succès',
        message: config.successMessage,
        color: 'green',
      });
      utils.projects.get.invalidate({ slug: project.slug });
    },
    onError: error => {
      notifications.show({
        title: 'Erreur',
        message: error.message,
        color: 'red',
      });
    },
  });

  const toggleBooleanState = async (field: Field, currentValue: boolean) => {
    const nextValue = !currentValue;
    const config = fieldConfigs[field];

    if (nextValue && config.getBlockingCondition) {
      const blockingCondition = config.getBlockingCondition(project);

      if (blockingCondition && blockingCondition.requirements.length > 0) {
        notifications.show({
          title: blockingCondition.title,
          message: (
            <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
              {`- ${blockingCondition.requirements.join('\n- ')}`}
            </Text>
          ),
          color: 'orange',
        });
        return;
      }
    }

    setPendingField(field);

    try {
      await updateFriezeField.mutateAsync({
        id: project.id,
        field,
        value: nextValue,
      } as UpdateFriezeFieldInput);
    } finally {
      setPendingField(null);
    }
  };

  return {
    isFieldPending: (field: Field) =>
      updateFriezeField.isPending && pendingField === field,
    toggleBooleanState,
  };
}
